import matchers from "../validate-props/matchers";

export const dateFormatChars = {
    YYYY: "YYYY",
    MM: "MM",
    DD: "DD",
};

export const dateFormatCharsRegex = {
    YYYY: /YYYY/g,
    MM: /MM/g,
    DD: /DD/g,
};

export const comparisonPrecision = {
    year: "year",
    month: "month",
    day: "day",
};

/**
 * Error treatment to toLocaleDateString if a wrong locale is informed
 * @param {Date} date
 * @param {String} locale - locale string
 * @param {Object} config - toLocaleDateString config
 * @returns {String}
 */
export const getLocaleDateString = (date, locale, config) => {
    try {
        return new Date(date).toLocaleDateString(locale, config);
    } catch (e) {
        return new Date(date).toLocaleDateString("en-US", config);
    }
};

/**
 * Add leading zero to days
 * @param {Number} day - From 1 to 31
 * @returns {String}
 */
export const getTwoDigitDay = (day) => {
    return ("0" + day).slice(-2);
};

/**
 * Add leading zero to zero index months
 * @param {Number} month - From 0 to 11
 * @returns {String}
 */
export const getTwoDigitMonth = (month) => {
    return ("0" + (month + 1)).slice(-2);
};

/**
 * Get fullYear, month and day from a date
 * @param {Date} date
 * @returns {Array}
 */
export const getDateVariables = (date) => {
    return [date.getFullYear(), date.getMonth(), date.getDate()];
};

/**
 * Get total days or month last day
 * @param {Date} date
 * @returns {Number}
 */
export const getTotalDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

/**
 * Get Date object on the first day of month
 * @param {Date} date
 * @returns {Date}
 */
export const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Get Date object from a string based on a format
 * NOTE: It will only return a date if the user has the 3 instances MM - DD - YYYY
 * @param {String} value
 * @param {String} format - Date format using available dateFormatChars characters
 * @returns {Date|Null}
 */
export const getDateFromString = (value, format) => {
    let year, month, day;
    let isValidYear = true;
    let isValidMonth = true;
    let isValidDay = true;
    let isFormattedDateValid = true;

    if (format.includes(dateFormatChars.YYYY)) {
        const yearIndex = dateFormatCharsRegex.YYYY.exec(format).index;
        dateFormatCharsRegex.YYYY.lastIndex = 0; // .exec change last index, reset it
        year = parseInt(value.substr(yearIndex, dateFormatChars.YYYY.length));
        isValidYear = year.toString().length === 4;
    }

    if (format.includes(dateFormatChars.MM)) {
        const monthIndex = dateFormatCharsRegex.MM.exec(format).index;
        dateFormatCharsRegex.MM.lastIndex = 0; // .exec change last index, reset it
        month = parseInt(value.substr(monthIndex, dateFormatChars.MM.length)) - 1;
        isValidMonth = month >= 0 && month <= 11;
    }

    if (format.includes(dateFormatChars.DD)) {
        const dayIndex = dateFormatCharsRegex.DD.exec(format).index;
        dateFormatCharsRegex.DD.lastIndex = 0; // .exec change last index, reset it
        day = parseInt(value.substr(dayIndex, dateFormatChars.DD.length));
        isValidDay = isValidYear && isValidMonth && new Date(year, month, day).getDate() === day;
    }

    isFormattedDateValid = isValidYear && isValidMonth && isValidDay;

    return isFormattedDateValid ? new Date(year, month, day) : null;
};

/**
 * Build a input mask from determined format
 * @param {String} format - Date format using available dateFormatChars characters
 * @returns {String}
 */
export const buildMaskBasedOnDateFormat = (format) => {
    let mask = format;

    mask = mask.replace(dateFormatCharsRegex.YYYY, "9999");
    mask = mask.replace(dateFormatCharsRegex.MM, "99");
    mask = mask.replace(dateFormatCharsRegex.DD, "99");

    return mask;
};

/**
 * Format a date according to a format using dateFormatCharsRegex
 * @param {Date} date
 * @param {String} format - Date format using available dateFormatChars characters
 * @returns {String}
 */
export const formatDate = (date, format) => {
    const [fullYear, month, day] = getDateVariables(date);

    format = format.replace(dateFormatCharsRegex.YYYY, fullYear);
    format = format.replace(dateFormatCharsRegex.MM, getTwoDigitMonth(month));
    format = format.replace(dateFormatCharsRegex.DD, getTwoDigitDay(day));

    return format;
};

/**
 * Check if date and comparison are equals depending on precision parameter
 * @param {Date} date
 * @param {Date} comparison
 * @param {String} precision - comparisonPrecision type
 * @returns {Boolean}
 */
export const isSameDate = (date, comparison, precision = comparisonPrecision.day) => {
    if (!matchers.date(date) || !matchers.date(comparison)) return false;

    const [dateFullYear, dateMonth, dateDay] = getDateVariables(date);
    const [comparisonFullYear, comparisonMonth, comparisonDay] = getDateVariables(comparison);

    if (precision === comparisonPrecision.year) {
        return dateFullYear === comparisonFullYear;
    }

    if (precision === comparisonPrecision.month) {
        return dateFullYear === comparisonFullYear && dateMonth === comparisonMonth;
    }

    if (precision === comparisonPrecision.day) {
        return (
            dateFullYear === comparisonFullYear &&
            dateMonth === comparisonMonth &&
            dateDay === comparisonDay
        );
    }

    console.error("Invalid precision");
    return false;
};

/**
 * Check if date is after comparison depending on precision parameter
 * @param {Date} date
 * @param {Date} comparison
 * @param {String} precision - comparisonPrecision type
 * @returns {Boolean}
 */
export const isDateAfter = (date, comparison, precision = comparisonPrecision.day) => {
    if (!matchers.date(date) || !matchers.date(comparison)) return false;

    const [dateFullYear, dateMonth, dateDay] = getDateVariables(date);
    const [comparisonFullYear, comparisonMonth, comparisonDay] = getDateVariables(comparison);

    if (precision === comparisonPrecision.year) {
        return dateFullYear > comparisonFullYear;
    }

    if (precision === comparisonPrecision.month) {
        return (
            dateFullYear > comparisonFullYear ||
            (dateFullYear >= comparisonFullYear && dateMonth > comparisonMonth)
        );
    }

    if (precision === comparisonPrecision.day) {
        return (
            dateFullYear > comparisonFullYear ||
            (dateFullYear >= comparisonFullYear && dateMonth > comparisonMonth) ||
            (dateFullYear >= comparisonFullYear &&
                dateMonth >= comparisonMonth &&
                dateDay > comparisonDay)
        );
    }

    console.error("Invalid precision");
    return false;
};

/**
 * Check if date is before comparison depending on precision parameter
 * @param {Date} date
 * @param {Date} comparison
 * @param {String} precision - comparisonPrecision type
 * @returns {Boolean}
 */
export const isDateBefore = (date, comparison, precision = comparisonPrecision.day) => {
    if (!matchers.date(date) || !matchers.date(comparison)) return false;

    const [dateFullYear, dateMonth, dateDay] = getDateVariables(date);
    const [comparisonFullYear, comparisonMonth, comparisonDay] = getDateVariables(comparison);

    if (precision === comparisonPrecision.year) {
        return dateFullYear < comparisonFullYear;
    }

    if (precision === comparisonPrecision.month) {
        return (
            dateFullYear < comparisonFullYear ||
            (dateFullYear === comparisonFullYear && dateMonth < comparisonMonth)
        );
    }

    if (precision === comparisonPrecision.day) {
        return (
            dateFullYear < comparisonFullYear ||
            (dateFullYear <= comparisonFullYear && dateMonth < comparisonMonth) ||
            (dateFullYear <= comparisonFullYear &&
                dateMonth <= comparisonMonth &&
                dateDay < comparisonDay)
        );
    }

    console.error("Invalid precision");
    return false;
};

/**
 * Check if date is same or after comparison depending on precision parameter
 * @param {Date} date
 * @param {Date} comparison
 * @param {String} precision - comparisonPrecision type
 * @returns {Boolean}
 */
export const isSameDateOrAfter = (date, comparison, precision = comparisonPrecision.day) => {
    return isSameDate(date, comparison, precision) || isDateAfter(date, comparison, precision);
};

/**
 * Check if date is same or before comparison depending on precision parameter
 * @param {Date} date
 * @param {Date} comparison
 * @param {String} precision - comparisonPrecision type
 * @returns {Boolean}
 */
export const isSameDateOrBefore = (date, comparison, precision = comparisonPrecision.day) => {
    return isSameDate(date, comparison, precision) || isDateBefore(date, comparison, precision);
};
